/* ==========================================================================
   IRON FIT — the 3D viewer, shared

   One implementation used by both the machine pages and the package pages:
   the "See it in 3D" button, the full-screen white stage, the Specs toggle,
   the labels pinned to parts and the height / width / depth rules.

   Call it with a spec object:

     IFZ3D.attach({
       name:  'Dual Cable Crossover',
       model: 'assets/ifz-crossover-3d.glb?v=4',
       view:  '-35deg 76deg 115%',      // the angle the labels are tuned to
       rule:  { height: {...}, width: {...}, depth: {...} },
       spots: [ { l: 'Adjustable pulleys', p: [x, y, z] }, ... ]
     }, hostElement);

   Positions in `rule` and `spots` are fractions of the model's own bounding
   box, so they survive whatever scale and origin the exporter used.
   ========================================================================== */
window.IFZ3D = (function () {
  'use strict';

  function $(s) { return document.querySelector(s); }

  /* ------------------------------------------------------------------
     The machines that have a 3D model, and where their labels sit.
     Kept here rather than in the machine page, because the package
     pages show the very same models and must not carry a second copy
     of these numbers.
     ------------------------------------------------------------------ */
  var SPECS = {
    compact: {
      name: 'Compact Single-Stack Gym',
      model: 'assets/ifz-compact-3d.glb?v=4',
      lite:  'assets/ifz-compact-3d-lite.glb?v=1',
      view: '45deg 76deg 118%',
      rule: {
        height: { l: '226 cm', a: [0.50, 1.00, 0.50], b: [0.50, 0.00, 0.50] },
        width:  { l: '94 cm',  a: [0.16, 0.02, 0.50], b: [0.84, 0.02, 0.50] },
        depth:  { l: '100 cm', a: [0.96, 0.02, 0.04], b: [0.96, 0.02, 0.96] }
      },
      spots: [
        { l: 'Lat pulldown bar',      p: [0.50, 0.96, 0.50] },
        { l: 'Built-in weight stack', p: [0.52, 0.34, 0.42] },
        { l: 'Leg rollers',           p: [0.74, 0.22, 0.34] }
      ]
    },
    crossover: {
      name: 'Dual Cable Crossover',
      model: 'assets/ifz-crossover-3d.glb?v=4',
      lite:  'assets/ifz-crossover-3d-lite.glb?v=1',
      view: '45deg 76deg 118%',
      rule: {
        height: { l: '210 cm', a: [0.50, 1.00, 0.50], b: [0.50, 0.00, 0.50] },
        width:  { l: '150 cm', a: [0.06, 0.02, 0.50], b: [0.94, 0.02, 0.50] },
        depth:  { l: '120 cm', a: [0.96, 0.02, 0.04], b: [0.96, 0.02, 0.96] }
      },
      spots: [
        { l: 'Multi-grip pull-up bar', p: [0.50, 0.97, 0.50] },
        { l: '2 × 75 kg stacks',       p: [0.42, 0.30, 0.42] },
        { l: 'Adjustable pulleys',     p: [0.14, 0.70, 0.60] }
      ]
    },
    smith: {
      name: 'Multifunctional Smith Machine',
      model: 'assets/ifz-smith-3d.glb?v=4',
      lite:  'assets/ifz-smith-3d-lite.glb?v=1',
      view: '45deg 76deg 118%',
      rule: {
        height: { l: '210 cm', a: [0.50, 1.00, 0.50], b: [0.50, 0.00, 0.50] },
        width:  { l: '150 cm', a: [0.04, 0.02, 0.50], b: [0.96, 0.02, 0.50] },
        depth:  { l: '118 cm', a: [0.96, 0.02, 0.04], b: [0.96, 0.02, 0.96] }
      },
      spots: [
        { l: 'Guided Smith bar',   p: [0.50, 0.72, 0.30] },
        { l: 'Cable stations',     p: [0.10, 0.66, 0.60] },
        { l: 'J-hooks & horns',    p: [0.88, 0.40, 0.60] }
      ]
    }
  };

  /* ------------------------------------------------------------------
     3D MODEL

     A machine with a .glb gets a "See it in 3D" button under the spin
     viewer. It opens full screen, model centred and as big as the screen
     allows, because the point of 3D is to walk around the thing — a
     postage stamp inside the page would be worse than the photos.

     model-viewer is fetched the first time someone asks for it, not on
     page load: it is ~150 KB and most visitors never open the 3D.
     ------------------------------------------------------------------ */
  /* Vendored rather than pulled off a CDN: the store should not go dark
     because unpkg is having a bad day, and this keeps working offline. */
  /* Through IFZA: on Shopify this file lives on the CDN, not in an
     assets/ folder, and a raw path 404s — which kills every 3D view. */
  var MV = window.IFZA('assets/model-viewer.min.js');
  var MV_CDN = 'https://unpkg.com/@google/model-viewer@3.5.0/dist/model-viewer.min.js';
  var mvLoading = null;

  /* ------------------------------------------------------------------
     MESHOPT DECODER — local, and it must be set before model-viewer runs.

     The machine models are the full-resolution meshes now (~1.9M triangles
     each, against the 153k the site used to ship) and they are stored with
     EXT_meshopt_compression, which is what keeps that many triangles down
     to a downloadable file. model-viewer has NO built-in location for the
     meshopt decoder — unlike Draco, which it would silently fetch from
     gstatic — so without this line the models simply fail to parse.

     Pointed at our own copy for the same reason model-viewer itself is
     vendored: the store must not need a CDN to show a machine, and the
     handoff package promises it works offline. The decoder is 28 KB and
     is only fetched alongside the model itself.
     ------------------------------------------------------------------ */
  window.ModelViewerElement = window.ModelViewerElement || {};
  if (!window.ModelViewerElement.meshoptDecoderLocation) {
    window.ModelViewerElement.meshoptDecoderLocation = window.IFZA('assets/meshopt_decoder.js');
    /* The models are Draco now — half the download of meshopt for the
       same 1.9M triangles. Vendored, so nothing is fetched from gstatic. */
    window.ModelViewerElement.dracoDecoderLocation = window.IFZA('assets/');
  }

  function loadViewer() {
    if (window.customElements && customElements.get('model-viewer')) return Promise.resolve();
    if (mvLoading) return mvLoading;
    mvLoading = new Promise(function (res, rej) {
      function add(src, onFail) {
        var sc = document.createElement('script');
        sc.type = 'module';
        sc.src = src;
        sc.onload = res;
        sc.onerror = onFail;
        document.head.appendChild(sc);
      }
      add(MV, function () { add(MV_CDN, rej); });
    });
    return mvLoading;
  }

  /* ------------------------------------------------------------------
     WARM-UP — this is what the twenty-second wait actually was.

     Nothing was fetched until the button was pressed, so a ~6 MB model,
     the 150 KB viewer and the 340 KB Draco decoder all sat between the
     tap and the picture, on a phone, on mobile data. Draco halved the
     model; this takes most of what is left off the critical path by
     starting the download BEFORE the tap rather than making it smaller.

     Two triggers, cheapest first:

       touch / hover on the button — worth a few hundred milliseconds,
         and on a phone touchstart lands well before the click does.

       idle, once the page has finished loading — worth the whole
         download. Skipped on Save-Data and on 2G, where several MB
         nobody asked for is a rude way to spend someone's bundle.

     Theme assets come back cache-control: max-age=31557600, so the fetch
     here and model-viewer's fetch afterwards are the same cache entry —
     as long as the URL matches to the character, which is why both sides
     go through IFZA rather than building the path themselves.

     rel=prefetch would be the tidier tool and is deliberately not used:
     Safari does not support it, and Safari is most of this shop's phones.
     ------------------------------------------------------------------ */
  var warmed = {};
  var idleWarmDone = false;

  function fetchQuietly(url) {
    if (!url || !window.fetch) return Promise.resolve();
    if (warmed[url]) return warmed[url];
    /* The body has to be drained or the response is not reliably written
       to cache. Returning nothing from the last then() matters: resolve
       with the ArrayBuffer and this promise pins several MB in memory for
       as long as the page is open, because warmed[] holds onto it. */
    warmed[url] = fetch(url)
      .then(function (r) { return r.arrayBuffer(); })
      .then(function () {})
      .catch(function () {});
    return warmed[url];
  }

  function warm(p) {
    if (!p || !p.model) return;
    loadViewer();                                   /* the element, ~150 KB */
    fetchQuietly(window.IFZA('assets/draco_wasm_wrapper.js'));
    fetchQuietly(window.IFZA('assets/draco_decoder.wasm'));

    /* Strictly one after the other, and this is the whole point. Fired in
       parallel, the 6 MB full mesh competes for the same pipe as the 1 MB
       light one — so the file the visitor is actually waiting on arrives
       LATER than if we had never prefetched the big one at all. */
    if (!p.lite) { fetchQuietly(window.IFZA(p.model)); return; }
    fetchQuietly(window.IFZA(p.lite)).then(function () {
      fetchQuietly(window.IFZA(p.model));
    });
  }

  function warmWhenIdle(p) {
    if (idleWarmDone) return;                       /* one model per page */
    var c = navigator.connection;
    if (c && (c.saveData || /2g/.test(c.effectiveType || ''))) return;
    idleWarmDone = true;

    function go() {
      /* behind the page's own images and fonts, never in front of them */
      var start = function () { warm(p); };
      if (window.requestIdleCallback) requestIdleCallback(start, { timeout: 3000 });
      else setTimeout(start, 1200);
    }
    if (document.readyState === 'complete') setTimeout(go, 800);
    else window.addEventListener('load', function () { setTimeout(go, 800); });
  }

  /* ------------------------------------------------------------------
     THE SPEC OVERLAY

     Two things sit on top of the model when Specs is on: labels pinned to
     parts, and dimension lines — the height and the width, drawn the way a
     drawing does it, with ticks at each end and the measurement in the
     middle.

     Both are anchored the same way: an invisible hotspot is placed at each
     end, model-viewer moves those with the model, and the lines are redrawn
     from where those anchors actually land on screen. That is why the rule
     still fits when you turn or zoom the machine — it is measuring the model,
     not painted on at fixed coordinates.

     Positions are fractions of the model's own bounding box, because two
     exports of two machines share neither scale nor origin.
     ------------------------------------------------------------------ */

  /* the angle the labels are tuned against, unless a machine names its own */
  var SPEC_VIEW = '45deg 76deg 118%';
  var SVGNS = 'http://www.w3.org/2000/svg';

  function anchor(mv, name, pos, dims, centre) {
    var el = document.createElement('div');
    el.className = 'm3d__anchor';
    el.setAttribute('slot', 'hotspot-' + name);
    el.dataset.position =
      (centre.x + (pos[0] - 0.5) * dims.x) + 'm ' +
      (centre.y + (pos[1] - 0.5) * dims.y) + 'm ' +
      (centre.z + (pos[2] - 0.5) * dims.z) + 'm';
    mv.appendChild(el);
    return el;
  }

  function addSpots(mv, p, box) {
    if (!mv.getDimensions) return;
    /* model-viewer fires `load` more than once. Without this the labels were
       built twice and the toggle was wired twice, so one tap turned the specs
       on and straight back off again. */
    if (mv.__ifzSpots) return;
    mv.__ifzSpots = 1;
    var dims = mv.getDimensions();
    var centre = mv.getBoundingBoxCenter();
    if (!dims || !dims.y) return;

    /* ---------------- part labels ---------------- */
    (p.spots || []).forEach(function (s, i) {
      var x = centre.x + (s.p[0] - 0.5) * dims.x;
      var y = centre.y + (s.p[1] - 0.5) * dims.y;
      var z = centre.z + (s.p[2] - 0.5) * dims.z;
      var nx = x - centre.x, ny = y - centre.y, nz = z - centre.z;
      var len = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;

      var hot = document.createElement('button');
      hot.className = 'm3d__spot';
      hot.type = 'button';
      hot.tabIndex = -1;
      hot.setAttribute('slot', 'hotspot-' + i);
      hot.dataset.position = x + 'm ' + y + 'm ' + z + 'm';
      hot.dataset.normal = (nx / len) + ' ' + (ny / len) + ' ' + (nz / len);
      hot.innerHTML = '<i aria-hidden="true"></i><span>' + s.l + '</span>';
      mv.appendChild(hot);
    });

    /* ---------------- the dimension lines ---------------- */
    var rule = p.rule;
    var svg = null, ends = null;

    if (rule) {
      ends = {
        ht: anchor(mv, 'ht', rule.height.a, dims, centre),
        hb: anchor(mv, 'hb', rule.height.b, dims, centre),
        wl: anchor(mv, 'wl', rule.width.a, dims, centre),
        wr: anchor(mv, 'wr', rule.width.b, dims, centre),
        df: anchor(mv, 'df', rule.depth.a, dims, centre),
        db: anchor(mv, 'db', rule.depth.b, dims, centre)
      };

      /* the eight corners of the box, used only to find where the machine
         actually ends on screen at whatever angle it is being viewed from */
      var corners = [];
      [0, 1].forEach(function (cx) {
        [0, 1].forEach(function (cy) {
          [0, 1].forEach(function (cz) {
            corners.push(anchor(mv, 'c' + cx + cy + cz, [cx, cy, cz], dims, centre));
          });
        });
      });

      svg = document.createElementNS(SVGNS, 'svg');
      svg.setAttribute('class', 'm3d__rule');
      svg.innerHTML =
        '<defs><marker id="m3dArrow" viewBox="0 0 8 8" refX="7" refY="4" ' +
          'markerWidth="7" markerHeight="7" orient="auto-start-reverse">' +
          '<path d="M0 0 8 4 0 8z" fill="#35e02a"/></marker></defs>' +
        '<g class="m3d__rule-h"><path/><path class="tick"/><path class="tick"/></g>' +
        '<g class="m3d__rule-w"><path/><path class="tick"/><path class="tick"/></g>' +
        '<g class="m3d__rule-d"><path/><path class="tick"/><path class="tick"/></g>';
      box.querySelector('.m3d__stage').appendChild(svg);

      var hLab = document.createElement('span');
      hLab.className = 'm3d__dim m3d__dim--h';
      hLab.textContent = rule.height.l;
      var wLab = document.createElement('span');
      wLab.className = 'm3d__dim m3d__dim--w';
      wLab.textContent = rule.width.l;
      var dLab = document.createElement('span');
      dLab.className = 'm3d__dim m3d__dim--d';
      dLab.textContent = rule.depth.l;
      box.querySelector('.m3d__stage').appendChild(hLab);
      box.querySelector('.m3d__stage').appendChild(wLab);
      box.querySelector('.m3d__stage').appendChild(dLab);

      var GAP = 26, TICK = 9;

      function draw() {
        if (!mv.classList.contains('show-spots')) return;
        var host = box.querySelector('.m3d__stage').getBoundingClientRect();
        function pt(el) {
          var r = el.getBoundingClientRect();
          return { x: r.left + r.width / 2 - host.left, y: r.top + r.height / 2 - host.top };
        }
        var ht = pt(ends.ht), hb = pt(ends.hb), wl = pt(ends.wl), wr = pt(ends.wr);

        /* where the machine's outline sits on screen right now */
        var out = corners.map(pt);
        var left = Math.min.apply(null, out.map(function (q) { return q.x; }));
        var low  = Math.max.apply(null, out.map(function (q) { return q.y; }));

        svg.setAttribute('viewBox', '0 0 ' + host.width + ' ' + host.height);

        /* Height: a vertical rule standing clear of the machine's left edge,
           but never so far left that its own label falls off the stage. */
        var hx = Math.max(left - GAP, 50);
        var hg = svg.querySelector('.m3d__rule-h');
        hg.children[0].setAttribute('d', 'M' + hx + ' ' + ht.y + 'V' + hb.y);
        hg.children[1].setAttribute('d', 'M' + (hx - TICK) + ' ' + ht.y + 'h' + (TICK * 2));
        hg.children[2].setAttribute('d', 'M' + (hx - TICK) + ' ' + hb.y + 'h' + (TICK * 2));
        hLab.style.left = hx + 'px';
        hLab.style.top = ((ht.y + hb.y) / 2) + 'px';

        /* width: a horizontal rule under the whole machine */
        var wy = Math.min(low + GAP, host.height - 26);
        var wg = svg.querySelector('.m3d__rule-w');
        wg.children[0].setAttribute('d', 'M' + wl.x + ' ' + wy + 'H' + wr.x);
        wg.children[1].setAttribute('d', 'M' + wl.x + ' ' + (wy - TICK) + 'v' + (TICK * 2));
        wg.children[2].setAttribute('d', 'M' + wr.x + ' ' + (wy - TICK) + 'v' + (TICK * 2));
        wLab.style.left = ((wl.x + wr.x) / 2) + 'px';
        wLab.style.top = wy + 'px';

        /* Depth runs away from you, so its rule is drawn along the same
           diagonal the model's own depth axis makes on screen, pushed
           outwards from the middle of the machine so it clears the frame. */
        var df = pt(ends.df), db = pt(ends.db);
        var vx = db.x - df.x, vy = db.y - df.y;
        var vlen = Math.sqrt(vx * vx + vy * vy) || 1;
        var perpX = -vy / vlen, perpY = vx / vlen;

        var midX = (df.x + db.x) / 2, midY = (df.y + db.y) / 2;
        var cenX = (left + Math.max.apply(null, out.map(function (q) { return q.x; }))) / 2;
        var cenY = (Math.min.apply(null, out.map(function (q) { return q.y; })) + low) / 2;
        var away = ((midX - cenX) * perpX + (midY - cenY) * perpY) >= 0 ? 1 : -1;
        var ox = perpX * away * GAP, oy = perpY * away * GAP;

        var d1 = { x: df.x + ox, y: df.y + oy }, d2 = { x: db.x + ox, y: db.y + oy };

        /* the depth rule leans out to the back-right and was running off the
           edge of the stage — slide it back until both ends are on screen */
        var over = Math.max(d1.x, d2.x) - (host.width - 16);
        if (over > 0) { d1.x -= over; d2.x -= over; ox -= over; }
        var under = 16 - Math.min(d1.x, d2.x);
        if (under > 0) { d1.x += under; d2.x += under; ox += under; }
        var below = Math.max(d1.y, d2.y) - (host.height - 16);
        if (below > 0) { d1.y -= below; d2.y -= below; oy -= below; }
        var dg = svg.querySelector('.m3d__rule-d');
        dg.children[0].setAttribute('d', 'M' + d1.x + ' ' + d1.y + 'L' + d2.x + ' ' + d2.y);
        dg.children[1].setAttribute('d', 'M' + (d1.x - perpX * TICK) + ' ' + (d1.y - perpY * TICK) +
                                         'L' + (d1.x + perpX * TICK) + ' ' + (d1.y + perpY * TICK));
        dg.children[2].setAttribute('d', 'M' + (d2.x - perpX * TICK) + ' ' + (d2.y - perpY * TICK) +
                                         'L' + (d2.x + perpX * TICK) + ' ' + (d2.y + perpY * TICK));
        dLab.style.left = Math.min(host.width - 46, Math.max(46, midX + ox)) + 'px';
        dLab.style.top = Math.min(host.height - 22, midY + oy) + 'px';
      }

      mv.addEventListener('camera-change', draw);
      window.addEventListener('resize', draw);
      box.__ifzDrawRule = draw;
    }

    /* The button was wired up here, inside the model's load handler, which
       meant a tap that landed while the 57 MB file was still arriving did
       nothing at all. The switch now lives with the overlay and this only
       hands it the machinery once there is something to show. */
    var ctl = box.__ifzSpecs;
    ctl.ready = true;
    ctl.apply = function (on) {
      mv.classList.toggle('show-spots', on);
      box.classList.toggle('has-specs', on);
      if (on) {
        mv.removeAttribute('auto-rotate');
        mv.cameraOrbit = p.view || SPEC_VIEW;
        setTimeout(function () { if (box.__ifzDrawRule) box.__ifzDrawRule(); }, 60);
        setTimeout(function () { if (box.__ifzDrawRule) box.__ifzDrawRule(); }, 700);
      } else {
        mv.setAttribute('auto-rotate', '');
      }
    };
    if (ctl.on) ctl.apply(true);          /* asked for it while it was loading */
  }

  /* ------------------------------------------------------------------
     THE UPGRADE — why there are two models per machine.

     The full mesh is ~1.9M triangles and 5-7 MB. Compressed as hard as it
     can be compressed without throwing detail away, that is still twenty
     seconds of staring at a progress bar on Lebanese mobile data, and a
     progress bar is not a machine.

     So the viewer opens on a ~1 MB simplified build, which lands in about
     two seconds, and the full mesh is fetched behind it and swapped in
     when it arrives. Nobody waits for the full mesh; they are already
     turning the machine while it downloads.

     Nothing is lost: what the visitor ends up looking at is the same
     1.9M-triangle model as before. It simply is not the first thing they
     are made to wait for.

     The swap goes through a blob rather than handing model-viewer the URL
     so that the light model stays on screen for the whole download — set
     src to a remote file and the viewer blanks while it fetches.
     ------------------------------------------------------------------ */
  function upgradeToFull(mv, p, box) {
    if (!p.lite || mv.__ifzFull || !window.fetch) return;
    mv.__ifzFull = 1;

    var stage = box.querySelector('.m3d__stage');
    var chip = document.createElement('span');
    chip.className = 'm3d__sharpen';
    chip.innerHTML = '<i aria-hidden="true"></i><span>Loading full detail</span><b>0%</b>';
    stage.appendChild(chip);

    var pctEl = chip.querySelector('b');

    function fail() { if (chip.parentNode) chip.parentNode.removeChild(chip); }

    fetch(window.IFZA(p.model)).then(function (r) {
      if (!r.ok) throw new Error('http ' + r.status);
      var total = +(r.headers.get('content-length') || 0);
      /* No reader, or no length to measure against: still works, the
         percentage just sits still until it is done. */
      if (!r.body || !r.body.getReader || !total) return r.blob();
      var reader = r.body.getReader();
      var chunks = [], got = 0;
      return (function pump() {
        return reader.read().then(function (res) {
          if (res.done) return new Blob(chunks);
          chunks.push(res.value);
          got += res.value.length;
          if (pctEl) pctEl.textContent = Math.round(got / total * 100) + '%';
          return pump();
        });
      })();
    }).then(function (blob) {
      var url = URL.createObjectURL(blob);
      /* hold the camera exactly where they left it across the swap */
      var orbit = mv.getCameraOrbit ? mv.getCameraOrbit().toString() : null;
      var fov = mv.getFieldOfView ? mv.getFieldOfView() : null;
      var spinning = mv.hasAttribute('auto-rotate');

      mv.addEventListener('load', function once() {
        mv.removeEventListener('load', once);
        if (orbit) mv.cameraOrbit = orbit;
        if (fov) mv.fieldOfView = fov + 'deg';
        if (spinning) mv.setAttribute('auto-rotate', '');
        URL.revokeObjectURL(url);
        chip.classList.add('is-done');
        setTimeout(fail, 1400);
        if (box.__ifzDrawRule) box.__ifzDrawRule();
      });
      mv.src = url;
    }).catch(fail);
  }

  function attach(p, host) {
    if (!p.model) return;

    var open = document.createElement('button');
    open.type = 'button';
    open.className = 'see3d';
    open.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M12 2.6 21 7.3v9.4L12 21.4 3 16.7V7.3Z"/><path d="M3 7.3 12 12l9-4.7M12 12v9.4"/></svg>' +
      '<span>See it in 3D<small>Turn it, zoom in, look underneath</small></span>' +
      '<i aria-hidden="true">&rarr;</i>';

    if (host && host.appendChild) host.appendChild(open);

    /* Start fetching the moment there is any sign of intent. Both are
       passive so neither can delay the tap they are listening to. */
    ['pointerenter', 'touchstart', 'focus'].forEach(function (ev) {
      open.addEventListener(ev, function () { warm(p); }, { passive: true, once: true });
    });
    warmWhenIdle(p);

    var box = null;

    open.addEventListener('click', function () {
      if (box) { box.classList.add('is-on'); document.documentElement.classList.add('m3d-open'); return; }

      box = document.createElement('div');
      box.className = 'm3d is-on';
      box.innerHTML =
        '<div class="m3d__bar">' +
          '<span class="m3d__name">' + p.name + '</span>' +
          (p.spots ? '<button class="m3d__specs" type="button" aria-pressed="false">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" ' +
            'stroke-linecap="round" aria-hidden="true"><path d="M4 7h10M4 12h16M4 17h7"/>' +
            '<circle cx="18" cy="7" r="2"/><circle cx="14" cy="17" r="2"/></svg>' +
            '<span>Specs</span></button>' : '') +
          '<button class="m3d__x" type="button" aria-label="Close 3D view">&times;</button>' +
        '</div>' +
        '<div class="m3d__stage"><p class="m3d__load">Loading the 3D model' +
          '<b class="m3d__pct">0%</b>' +
          '<span class="m3d__track"><i></i></span>' +
          '<small>Almost there</small></p>' +
          (p.weight ? '<span class="m3d__weight"><b>' + p.weight + '</b>total weight</span>' : '') +
        '</div>' +
        '<p class="m3d__hint">Drag to turn &middot; pinch or scroll to zoom</p>';
      document.body.appendChild(box);
      document.documentElement.classList.add('m3d-open');

      function close() {
        box.classList.remove('is-on');
        document.documentElement.classList.remove('m3d-open');
      }
      /* state lives here so the switch works before the model has landed */
      box.__ifzSpecs = { ready: false, on: false, apply: function () {} };
      var specsBtn = box.querySelector('.m3d__specs');
      if (specsBtn) {
        specsBtn.addEventListener('click', function () {
          var ctl = box.__ifzSpecs;
          ctl.on = !ctl.on;
          specsBtn.setAttribute('aria-pressed', ctl.on ? 'true' : 'false');
          specsBtn.classList.toggle('is-on', ctl.on);
          if (ctl.ready) ctl.apply(ctl.on);
        });
      }

      box.querySelector('.m3d__x').addEventListener('click', close);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && box.classList.contains('is-on')) close();
      });

      loadViewer().then(function () {
        var mv = document.createElement('model-viewer');
        /* The LIGHT model goes in first — see upgradeToFull below. If there
           is no light build for this machine, this is the full one and
           nothing else happens. */
        mv.setAttribute('src', window.IFZA(p.lite || p.model));
        mv.setAttribute('alt', p.name + ' in 3D');
        mv.setAttribute('camera-controls', '');
        mv.setAttribute('touch-action', 'pan-y');
        mv.setAttribute('auto-rotate', '');
        mv.setAttribute('rotation-per-second', '18deg');
        mv.setAttribute('shadow-intensity', '1');
        mv.setAttribute('exposure', '1.15');
        mv.setAttribute('ar', '');
        mv.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
        mv.setAttribute('loading', 'eager');

        /* These are ~1.9M-triangle meshes — a real download and a real
           decode. A frozen "Loading…" for that long reads as broken, so
           show the actual figure model-viewer is already reporting. */
        mv.addEventListener('progress', function (e) {
          var pct = Math.round((e.detail && e.detail.totalProgress || 0) * 100);
          var num = box.querySelector('.m3d__pct');
          var bar = box.querySelector('.m3d__track i');
          if (num) num.textContent = pct + '%';
          if (bar) bar.style.width = pct + '%';
        });

        mv.addEventListener('load', function () {
          var l = box.querySelector('.m3d__load');
          if (l) l.remove();
          /* Anchors are ratios of the bounding box, and simplification
             leaves the bounding box where it was, so the labels can be
             hung off the light model and stay correct after the swap.
             Doing it now is what lets Specs work while the full mesh is
             still coming down. */
          addSpots(mv, p, box);
          upgradeToFull(mv, p, box);
        });
        box.querySelector('.m3d__stage').appendChild(mv);
      }).catch(function () {
        var l = box.querySelector('.m3d__load');
        if (l) l.textContent = 'The 3D view needs an internet connection.';
      });
    });
  }

  return {
    attach: attach,
    spec: function (id) { return SPECS[id] || null; }
  };
})();
